import { useMemo } from "react";
import type { ChartData, ChartOptions, Plugin } from "chart.js";
import { Line } from "react-chartjs-2";
import type { StatsChart } from "@/stores/ContractorDetailStore";

const MONTHS_SHORT = [
  "Янв",
  "Фев",
  "Мар",
  "Апр",
  "Май",
  "Июн",
  "Июл",
  "Авг",
  "Сен",
  "Окт",
  "Ноя",
  "Дек",
];

export function LineAreaChart({
  style,
  data,
  color = "#6366f1",
  formatValue,
  currentMonth,
  testId = "line-area-chart",
}: {
  style?: React.CSSProperties;
  data: StatsChart[];
  color?: string;
  formatValue?: (v: number) => string;
  currentMonth?: number;
  testId?: string;
}) {
  // показывать только прошедшие + текущий месяц
  const visibleData = currentMonth != null ? data.slice(0, currentMonth) : data;
  const vals = visibleData.map((d) => d.cumulative ?? d.value ?? 0);
  if (vals.length < 2) return null;

  const labels = visibleData.map(
    (d, i) => MONTHS_SHORT[(d.month ?? i + 1) - 1] ?? MONTHS_SHORT[i % 12],
  );

  const chartData: ChartData<"line"> = {
    labels,
    datasets: [
      {
        data: vals,
        borderColor: color,
        borderWidth: 2,
        fill: "origin",
        backgroundColor: `${color}2E`,
        tension: 0.4,
        cubicInterpolationMode: "monotone",
        pointRadius: 0,
        pointHoverRadius: 4,
        pointHoverBackgroundColor: color,
        pointHoverBorderColor: "#fff",
        pointHoverBorderWidth: 2,
      },
    ],
  };

  // Gradient fill plugin
  const gradientPlugin = useMemo(
    (): Plugin<"line"> => ({
      id: `lineGradient-${color}`,
      beforeDatasetDraw(chart) {
        const area = chart.chartArea;
        if (!area) return;
        const gradient = chart.ctx.createLinearGradient(
          0,
          area.top,
          0,
          area.bottom,
        );
        gradient.addColorStop(0, `${color}2E`);
        gradient.addColorStop(1, `${color}00`);
        chart.data.datasets[0].backgroundColor = gradient;
      },
    }),
    [color],
  );

  const options: ChartOptions<"line"> = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: "#ffffff",
        borderColor: "#e5e7eb",
        borderWidth: 1,
        titleColor: "#9ca3af",
        bodyColor: "#111827",
        titleFont: { size: 11, family: "var(--font-onest, sans-serif)" },
        bodyFont: {
          size: 13,
          family: "var(--font-onest, sans-serif)",
          weight: "bold",
        },
        padding: 10,
        cornerRadius: 8,
        displayColors: false,
        caretSize: 5,
        callbacks: {
          title: (ctxs) => ctxs[0].label ?? "",
          label: (ctx) => {
            const y = ctx.parsed.y;
            if (y == null) return "";
            return formatValue ? formatValue(y) : y.toLocaleString("ru-RU");
          },
        },
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: {
          font: { size: 9, family: "var(--font-onest, sans-serif)" },
          color: "#c4c4c4",
          maxRotation: 0,
        },
        border: { display: false },
      },
      y: { display: false },
    },
    interaction: { mode: "index", intersect: false },
  };

  return (
    <div style={style} className="scd-chart-area" data-testid={testId}>
      <Line data={chartData} options={options} plugins={[gradientPlugin]} />
    </div>
  );
}
